import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, 
  Download,
  Search,
  FileText,
  CheckCircle,
  Clock,
  Eye,
  Star
} from 'lucide-react';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { API_BASE } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';

const API = `${API_BASE}/api`;

export default function ProgrammeGradebookPage() {
  const navigate = useNavigate();
  const { programmeId } = useParams();
  const { token } = useAuth();

  const headers = { Authorization: `Bearer ${token}` };

  const [programme, setProgramme] = useState(null);
  const [gradebooks, setGradebooks] = useState([]);
  const [poeSubmissions, setPoeSubmissions] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('gradebook'); // gradebook, poe

  // Grading dialog
  const [gradingDialog, setGradingDialog] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [gradeValue, setGradeValue] = useState('');
  const [feedback, setFeedback] = useState('');
  const [submittingGrade, setSubmittingGrade] = useState(false);

  useEffect(() => {
    fetchData();
  }, [programmeId]);

  const fetchData = async () => {
    try {
      const [progRes, gradebookRes, poeRes] = await Promise.all([
        axios.get(`${API}/programmes/${programmeId}`, { headers }),
        axios.get(`${API}/programmes/${programmeId}/gradebook`, { headers }),
        axios.get(`${API}/programmes/${programmeId}/poe-submissions`, { headers })
      ]);
      setProgramme(progRes.data);
      setGradebooks(gradebookRes.data.gradebooks || []);
      setActivities(gradebookRes.data.activities || []);
      setPoeSubmissions(poeRes.data.submissions || []);
    } catch (error) {
      toast.error('Failed to load gradebook');
      navigate('/programmes');
    } finally {
      setLoading(false);
    }
  };

  const openGradingDialog = (submission) => {
    setSelectedSubmission(submission);
    setGradeValue(submission.grade_percentage?.toString() || '');
    setFeedback(submission.feedback || '');
    setGradingDialog(true);
  };

  const handleSubmitGrade = async () => {
    if (!gradeValue || isNaN(parseFloat(gradeValue))) {
      toast.error('Please enter a valid grade');
      return;
    }

    const grade = parseFloat(gradeValue);
    if (grade < 0 || grade > 100) {
      toast.error('Grade must be between 0 and 100');
      return;
    }

    setSubmittingGrade(true);
    try {
      await axios.post(
        `${API}/programmes/${programmeId}/poe/${selectedSubmission.submission_id}/grade`,
        {
          grade_percentage: grade,
          feedback: feedback
        },
        { headers }
      );

      toast.success('Grade submitted successfully');
      setGradingDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to submit grade');
    } finally {
      setSubmittingGrade(false);
    }
  };

  const downloadFile = (fileUrl) => {
    window.open(`${API_BASE}${fileUrl}`, '_blank');
  };

  const filteredGradebooks = gradebooks.filter(g => 
    g.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    g.learner_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredPoe = poeSubmissions.filter(s => 
    s.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.learner_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-zinc-800 rounded w-1/3" />
            <div className="h-64 bg-zinc-800 rounded" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate(`/admin/programmes/${programmeId}/edit`)}
              className="text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Programme
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white">
                Gradebook & Portfolio of Evidence
              </h1>
              <p className="text-sm text-zinc-400">
                {programme?.programme_name || 'Programme'}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-zinc-800">
          <button
            onClick={() => setActiveTab('gradebook')}
            className={`px-4 py-3 text-sm font-medium transition-colors relative ${
              activeTab === 'gradebook'
                ? 'text-teal-400'
                : 'text-zinc-400 hover:text-white'
            }`}
          >
            Gradebook ({gradebooks.length})
            {activeTab === 'gradebook' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-400" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('poe')}
            className={`px-4 py-3 text-sm font-medium transition-colors relative flex items-center gap-2 ${
              activeTab === 'poe'
                ? 'text-teal-400'
                : 'text-zinc-400 hover:text-white'
            }`}
          >
            Portfolio of Evidence ({poeSubmissions.length})
            {poeSubmissions.filter(s => s.status === 'submitted').length > 0 && (
              <Badge className="bg-amber-500/20 text-amber-400 text-xs">
                {poeSubmissions.filter(s => s.status === 'submitted').length} pending
              </Badge>
            )}
            {activeTab === 'poe' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-400" />
            )}
          </button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <Input
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-zinc-900 border-zinc-800 text-white"
            />
          </div>
        </div>

        {/* Gradebook Tab */}
        {activeTab === 'gradebook' && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Star className="h-5 w-5" />
                Learner Grades
              </CardTitle>
            </CardHeader>
            <CardContent>
              {filteredGradebooks.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-zinc-400">No gradebook entries yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800">
                      <TableHead className="text-zinc-400">Learner</TableHead>
                      <TableHead className="text-zinc-400">Email</TableHead>
                      <TableHead className="text-zinc-400">Activities Graded</TableHead>
                      <TableHead className="text-zinc-400">Overall Grade</TableHead>
                      <TableHead className="text-zinc-400">Last Updated</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredGradebooks.map((gradebook) => {
                      const gradedCount = Object.keys(gradebook.activity_grades || {}).length;
                      return (
                        <TableRow key={gradebook.gradebook_id} className="border-zinc-800">
                          <TableCell className="text-white font-medium">
                            {gradebook.learner_name || '-'}
                          </TableCell>
                          <TableCell className="text-zinc-400">{gradebook.email}</TableCell>
                          <TableCell className="text-zinc-400">
                            {gradedCount} / {activities.filter(a => a.allows_poe_submission).length}
                          </TableCell>
                          <TableCell>
                            {gradebook.overall_grade !== null ? (
                              <span className={`font-medium ${
                                gradebook.overall_grade >= 50 ? 'text-teal-400' : 'text-red-400'
                              }`}>
                                {gradebook.overall_grade}%
                              </span>
                            ) : (
                              <span className="text-zinc-500">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-zinc-400">
                            {formatDate(gradebook.updated_at)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}

        {/* POE Tab */}
        {activeTab === 'poe' && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Portfolio of Evidence Submissions
              </CardTitle>
            </CardHeader>
            <CardContent>
              {filteredPoe.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-zinc-400">No submissions yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800">
                      <TableHead className="text-zinc-400">Learner</TableHead>
                      <TableHead className="text-zinc-400">Activity</TableHead>
                      <TableHead className="text-zinc-400">Period</TableHead>
                      <TableHead className="text-zinc-400">Submitted</TableHead>
                      <TableHead className="text-zinc-400">File</TableHead>
                      <TableHead className="text-zinc-400">Status</TableHead>
                      <TableHead className="text-zinc-400">Grade</TableHead>
                      <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPoe.map((submission) => (
                      <TableRow key={submission.submission_id} className="border-zinc-800">
                        <TableCell className="text-white font-medium">
                          {submission.learner_name}
                        </TableCell>
                        <TableCell className="text-zinc-400">
                          {submission.activity_title || '-'}
                        </TableCell>
                        <TableCell className="text-zinc-400">
                          {submission.period_title || '-'}
                        </TableCell>
                        <TableCell className="text-zinc-400">
                          {formatDate(submission.submitted_at)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => downloadFile(submission.file_url)}
                            className="text-teal-400 hover:text-teal-300 h-8 px-2"
                          >
                            <Download className="h-4 w-4 mr-1" />
                            {submission.filename?.substring(0, 20)}...
                          </Button>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            className={submission.status === 'graded' 
                              ? 'bg-teal-500/20 text-teal-400' 
                              : 'bg-amber-500/20 text-amber-400'
                            }
                          >
                            {submission.status === 'graded' ? (
                              <><CheckCircle className="h-3 w-3 mr-1" /> Graded</>
                            ) : (
                              <><Clock className="h-3 w-3 mr-1" /> Pending</>
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {submission.grade_percentage !== null ? (
                            <span className={`font-medium ${
                              submission.grade_percentage >= 50 ? 'text-teal-400' : 'text-red-400'
                            }`}>
                              {submission.grade_percentage}%
                            </span>
                          ) : (
                            <span className="text-zinc-500">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openGradingDialog(submission)}
                            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                          >
                            {submission.status === 'graded' ? 'Edit Grade' : 'Grade'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}

        {/* Grading Dialog */}
        <Dialog open={gradingDialog} onOpenChange={setGradingDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
            <DialogHeader>
              <DialogTitle>Grade Submission</DialogTitle>
            </DialogHeader>
            
            {selectedSubmission && (
              <div className="space-y-4">
                <div className="p-4 bg-zinc-800 rounded-lg">
                  <p className="text-sm text-zinc-400">Learner</p>
                  <p className="text-white font-medium">{selectedSubmission.learner_name}</p>
                  <p className="text-sm text-zinc-400 mt-2">Activity</p>
                  <p className="text-white">{selectedSubmission.activity_title}</p>
                  <p className="text-sm text-zinc-400 mt-2">Submitted</p>
                  <p className="text-white">{formatDate(selectedSubmission.submitted_at)}</p>
                </div>

                <Button
                  variant="outline"
                  onClick={() => downloadFile(selectedSubmission.file_url)}
                  className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Submission ({selectedSubmission.filename})
                </Button>

                <div>
                  <Label className="text-zinc-400">Grade (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={gradeValue}
                    onChange={(e) => setGradeValue(e.target.value)}
                    placeholder="Enter grade (0-100)"
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  />
                </div>

                <div>
                  <Label className="text-zinc-400">Feedback (optional)</Label>
                  <Textarea
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    placeholder="Provide feedback to the learner..."
                    rows={3}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setGradingDialog(false)}
                className="border-zinc-700 text-zinc-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitGrade}
                disabled={submittingGrade}
                className="bg-teal-600 hover:bg-teal-700 text-white"
              >
                {submittingGrade ? 'Submitting...' : 'Submit Grade'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
